import { GetStaticPaths, GetStaticProps } from "next";
import {
  CopyProps,
  UtilityCopy,
  UtilityCopyProps,
} from "@bluebottlecoffee/design-system/components";
import { FunctionComponent } from "react";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import { REGION } from "../../../../lib/i18n";
import sanityClient from "../../../../lib/sanity-client";
import { Notice } from "../../../../lib/sanity-schema";
import { BaseProps } from "../../../../lib/sanity/shared";
import { filters, getSlug } from "../../../../lib/sanity/utils";
import {
  buildTranslationGroupQuery,
  TranslationGroupSanityType,
  toCopyObject,
} from "../../../../lib/sanity/translation-group-queries";
import { toUtilityCopyProps } from "../../../../lib/transformers/utility-copy";
import { getDefaultStaticProps } from "../../../../lib/utils";
import localization from "../../../../localization";

const { excludeDrafts, matchSlug, whereType } = filters;

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient().fetch<Notice["slug"]["current"][]>(`
    *[${whereType("notice")} && ${excludeDrafts}].${getSlug("slug")}`);

  const paths = localization.languages(REGION).flatMap((lang) =>
    slugs.map((slug) => ({
      params: {
        region: REGION,
        lang: lang.code,
        slug,
      },
    })),
  );

  return {
    paths,
    fallback: false,
  };
};

interface PageData extends BaseProps {
  notice: Notice;
  utilityTranslations: CopyProps;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { slug } = context.params;

  const { props: defaultProps } = await getDefaultStaticProps(context);

  const { lang } = defaultProps;

  const query = buildTranslationGroupQuery(lang, "UtilityPage");

  const utilityTranslations = await sanityClient()
    .fetch<TranslationGroupSanityType>(query)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<CopyProps>(lang, translations),
    );

  const notice = await sanityClient().fetch<Notice>(`
  *[
    ${whereType("notice")} && ${excludeDrafts} && ${matchSlug<Notice>(
      "slug",
      slug as string,
    )}
  ][0]`);

  return {
    props: {
      notice,
      utilityTranslations,
      ...defaultProps,
    },
  };
};

const NoticePage: FunctionComponent<PageData> = ({
  notice,
  utilityTranslations,
  ...layoutWrapperProps
}) => {
  const { region, lang } = layoutWrapperProps;

  const utilityCopyProps: UtilityCopyProps = {
    ...toUtilityCopyProps(notice.copy, region, lang),
    copyProps: utilityTranslations,
  };

  return (
    <>
      <CanonicalHead
        region={region}
        lang={lang}
        slug={`notice/${notice.slug.current}`}
      />
      <LayoutWrapper
        {...{
          ...layoutWrapperProps,
          pageTitle: notice.title[lang],
          metaTags: notice.metaTags,
        }}
      >
        <UtilityCopy {...utilityCopyProps} />
      </LayoutWrapper>
    </>
  );
};

export default NoticePage;
